import { useState, useEffect, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
  CreditCard, UserPlus, Loader2, Power, Wallet, CheckCircle2, Clock, AlertTriangle,
  Play, Copy, Trash2, Pencil, Search, CalendarClock,
} from "lucide-react";

const PLAN_OPTIONS = [
  { key: "trial", label: "Trial 6h — grátis" },
  { key: "mensal", label: "Mensal — R$ 59,90" },
  { key: "trimestral", label: "Trimestral — R$ 149,90" },
  { key: "semestral", label: "Semestral — R$ 269,90" },
  { key: "anual", label: "Anual — R$ 479,90" },
];
const PAID_PLANS = PLAN_OPTIONS.filter((p) => p.key !== "trial");
const PAYMENT_METHODS = ["Pix", "Dinheiro", "Cartão de crédito", "Cartão de débito", "Boleto", "Transferência", "Outro"];
const SORT_OPTIONS = [
  { key: "vencimento", label: "Vencimento" },
  { key: "name", label: "Nome" },
  { key: "status", label: "Status" },
];

const formatBRL = (v) => {
  const n = Number(v) || 0;
  try { return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }); }
  catch (e) { return "R$ " + n.toFixed(2).replace(".", ","); }
};
const formatDateBR = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};
const formatDateTimeBR = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${formatDateBR(iso)} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};
const dueInfo = (p) => {
  if (!p.plan_end) return null;
  const end = new Date((p.plan_end || "").slice(0, 10) + "T00:00:00");
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const days = Math.round((end - today) / 86400000);
  if (days < 0) return { level: "overdue", days: Math.abs(days) };
  if (days <= 7) return { level: "soon", days };
  return null;
};
const STATUS_STYLE = {
  ativo: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  trial: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  inadimplente: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  inativo: "bg-slate-500/15 text-slate-400 border-slate-500/30",
};
const STATUS_LABEL = { ativo: "Ativo", trial: "Trial", inadimplente: "Inadimplente", inativo: "Inativo" };
const STATUS_ORDER = { inadimplente: 0, trial: 1, ativo: 2, inativo: 3 };
const selectCls = "h-11 bg-[#181F2C] border-[#263147] text-slate-100 rounded-lg";

export default function Planos() {
  const [data, setData] = useState({ professors: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("vencimento");
  const [createOpen, setCreateOpen] = useState(false);
  const [createDraft, setCreateDraft] = useState({ email: "", name: "", plan: "trial" });
  const [payFor, setPayFor] = useState(null);
  const [payDraft, setPayDraft] = useState({ date: "", amount: "", plan: "mensal" });
  const [editFor, setEditFor] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [deleteFor, setDeleteFor] = useState(null);
  const [demos, setDemos] = useState([]);
  const [creatingDemo, setCreatingDemo] = useState(false);

  const loadDemos = useCallback(async () => {
    try { const { data } = await api.get(`/admin/demos`); setDemos(data.demos || []); }
    catch (e) { /* silencioso */ }
  }, []);
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/admin/professors`); setData(data); }
    catch { toast.error("Não foi possível carregar os professores."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); loadDemos(); }, [load, loadDemos]);

  const createDemo = async () => {
    setCreatingDemo(true);
    try {
      const { data } = await api.post(`/admin/demo`);
      const link = `${window.location.origin}/demo?token=${data.token}`;
      try { await navigator.clipboard.writeText(link); toast.success("Conta demo criada! Link copiado."); }
      catch { toast.success("Conta demo criada!"); }
      loadDemos();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao criar conta demo."); }
    finally { setCreatingDemo(false); }
  };
  const copyDemoLink = async (token) => {
    const link = `${window.location.origin}/demo?token=${token}`;
    try { await navigator.clipboard.writeText(link); toast.success("Link copiado!"); }
    catch { toast.error("Não foi possível copiar. Link: " + link); }
  };
  const removeDemo = async (id) => {
    try { await api.delete(`/admin/demo/${id}`); toast.success("Conta demo removida."); loadDemos(); }
    catch { toast.error("Falha ao remover conta demo."); }
  };

  const createProfessor = async () => {
    if (!createDraft.email.trim() || !createDraft.name.trim()) { toast.error("Preencha nome e e-mail."); return; }
    try {
      await api.post(`/admin/professors`, createDraft);
      toast.success("Professor cadastrado!");
      setCreateOpen(false); setCreateDraft({ email: "", name: "", plan: "trial" }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao cadastrar professor."); }
  };
  const toggleStatus = async (p) => {
    try { await api.patch(`/admin/professors/${p.id}/status`); toast.success("Acesso atualizado."); load(); }
    catch { toast.error("Falha ao atualizar acesso."); }
  };
  const openPay = (p) => {
    setPayFor(p);
    setPayDraft({ date: new Date().toISOString().slice(0, 10), amount: "", plan: p.plan && p.plan !== "trial" ? p.plan : "mensal" });
  };
  const savePayment = async () => {
    if (!payDraft.date || !payDraft.amount || parseFloat(payDraft.amount) <= 0) { toast.error("Informe data e valor."); return; }
    try {
      await api.post(`/admin/professors/${payFor.id}/payment`, { date: payDraft.date, amount: parseFloat(payDraft.amount), plan: payDraft.plan });
      toast.success("Pagamento registrado!");
      setPayFor(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao registrar pagamento."); }
  };

  const openEdit = (p) => {
    setEditFor(p);
    setEditDraft({
      name: p.name || "",
      plan: p.plan || "mensal",
      plan_price: p.plan_price != null ? String(p.plan_price) : "",
      plan_days: p.plan_days != null ? String(p.plan_days) : "",
      plan_start: (p.plan_start || "").slice(0, 10),
      plan_end: (p.plan_end || "").slice(0, 10),
      status: p.status === "inativo" ? "inativo" : "ativo",
      payment_method: p.payment_method || "Pix",
      notes: p.notes || "",
    });
  };
  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      await api.patch(`/admin/professors/${editFor.id}`, {
        name: editDraft.name,
        plan: editDraft.plan,
        plan_price: editDraft.plan_price === "" ? null : parseFloat(editDraft.plan_price),
        plan_days: editDraft.plan_days === "" ? null : parseInt(editDraft.plan_days, 10),
        plan_start: editDraft.plan_start,
        plan_end: editDraft.plan_end,
        status: editDraft.status,
        payment_method: editDraft.payment_method,
        notes: editDraft.notes,
      });
      toast.success("Professor atualizado!");
      setEditFor(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao atualizar professor."); }
    finally { setSavingEdit(false); }
  };
  const confirmDelete = async () => {
    const p = deleteFor;
    setDeleteFor(null);
    try {
      await api.delete(`/admin/professors/${p.id}`);
      toast.success("Professor excluído.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao excluir professor."); }
  };

  const s = data.summary || {};
  const filtered = useMemo(() => {
    let arr = [...(data.professors || [])];
    const q = search.trim().toLowerCase();
    if (q) arr = arr.filter((p) => (p.name || "").toLowerCase().includes(q) || (p.email || "").toLowerCase().includes(q));
    arr.sort((a, b) => {
      if (sortBy === "name") return (a.name || "").localeCompare(b.name || "");
      if (sortBy === "status") return (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9);
      return (a.plan_end || "").localeCompare(b.plan_end || "");
    });
    return arr;
  }, [data.professors, search, sortBy]);

  return (
    <div data-testid="planos-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-heading text-slate-100 mb-1 flex items-center gap-2">
            <CreditCard className="w-6 h-6 text-emerald-400" /> Planos
          </h1>
          <p className="text-sm text-slate-400">Gestão de professores e assinaturas.</p>
        </div>
        <Button data-testid="planos-new-professor-button" onClick={() => setCreateOpen(true)}
          className="h-11 bg-emerald-500 hover:bg-emerald-600 text-[#0A0D12] font-semibold rounded-lg px-4">
          <UserPlus className="w-4 h-4 mr-1" /> Novo professor
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div data-testid="summary-ativos" className="bg-[#0F1520] border border-[#263147] rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-1"><CheckCircle2 className="w-4 h-4 text-emerald-400" /><span className="text-xs text-slate-400">Ativos</span></div>
          <p className="text-2xl font-bold text-emerald-400 tabular-nums">{s.ativos || 0}</p>
        </div>
        <div data-testid="summary-inadimplentes" className="bg-[#0F1520] border border-[#263147] rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-1"><AlertTriangle className="w-4 h-4 text-amber-400" /><span className="text-xs text-slate-400">Inadimplentes</span></div>
          <p className="text-2xl font-bold text-amber-400 tabular-nums">{s.inadimplentes || 0}</p>
        </div>
        <div data-testid="summary-trial" className="bg-[#0F1520] border border-[#263147] rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-1"><Clock className="w-4 h-4 text-blue-400" /><span className="text-xs text-slate-400">Em trial</span></div>
          <p className="text-2xl font-bold text-blue-400 tabular-nums">{s.trial || 0}</p>
        </div>
      </div>

      {!loading && data.professors.length > 0 && (
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input data-testid="professor-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nome ou e-mail..." className={`${selectCls} pl-9`} />
          </div>
          <div className="sm:w-56">
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger data-testid="professor-sort-select" className={selectCls}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#181F2C] border-[#263147] text-slate-100">
                {SORT_OPTIONS.map((o) => <SelectItem key={o.key} value={o.key}>Ordenar por: {o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Carregando...</div>
      ) : data.professors.length === 0 ? (
        <div className="text-center py-16 text-slate-500 text-sm">Nenhum professor cadastrado ainda.</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-slate-500 text-sm">Nenhum professor encontrado para "{search}".</div>
      ) : (
        <div className="space-y-2">
          {filtered.map((p) => {
            const du = dueInfo(p);
            const rowAccent = du?.level === "overdue" ? "border-red-500/50 bg-red-500/[0.06]"
              : du?.level === "soon" ? "border-amber-500/50 bg-amber-500/[0.06]"
                : "border-[#263147]";
            const hasPayments = (p.payments || []).length > 0;
            return (
              <div key={p.id} data-testid="professor-row" className={`bg-[#0F1520] border rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3 ${rowAccent}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-slate-100 font-medium truncate">{p.name}</p>
                  <p className="text-xs text-slate-500 truncate">{p.email}</p>
                </div>
                <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
                  <div><p className="text-[11px] text-slate-500">Plano</p><p className="text-slate-200">{p.plan_label || "—"}{p.plan_price != null ? ` · ${formatBRL(p.plan_price)}` : ""}</p></div>
                  <div><p className="text-[11px] text-slate-500">Início</p><p className="text-slate-300 tabular-nums">{formatDateBR(p.plan_start)}</p></div>
                  <div>
                    <p className="text-[11px] text-slate-500">Vencimento</p>
                    <p className={`tabular-nums ${du?.level === "overdue" ? "text-red-400" : du?.level === "soon" ? "text-amber-400" : "text-slate-300"}`}>{formatDateBR(p.plan_end)}</p>
                  </div>
                  {du && (
                    <span data-testid="professor-due-alert" className={`text-[11px] flex items-center gap-1 px-2 py-0.5 rounded-full border ${du.level === "overdue" ? "bg-red-500/15 text-red-400 border-red-500/30" : "bg-amber-500/15 text-amber-400 border-amber-500/30"}`}>
                      <CalendarClock className="w-3 h-3" />
                      {du.level === "overdue" ? `Vencido há ${du.days}d` : `Vence em ${du.days}d`}
                    </span>
                  )}
                  <span data-testid="professor-status" className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_STYLE[p.status]}`}>{STATUS_LABEL[p.status]}</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" size="sm" data-testid="professor-edit-button" onClick={() => openEdit(p)}
                    className="h-9 bg-[#181F2C] border border-[#263147] text-slate-200 hover:bg-[#1E2738] rounded-lg px-3"><Pencil className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Editar</span></Button>
                  <Button type="button" size="sm" data-testid="professor-payment-button" onClick={() => openPay(p)}
                    className="h-9 bg-[#181F2C] border border-[#263147] text-slate-200 hover:bg-[#1E2738] rounded-lg px-3"><Wallet className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Pix</span></Button>
                  <Button type="button" size="sm" data-testid="professor-delete-button" disabled={hasPayments}
                    title={hasPayments ? "Não é possível excluir: há pagamento registrado" : "Excluir professor"}
                    onClick={() => setDeleteFor(p)}
                    className="h-9 bg-red-500/15 text-red-400 border border-red-500/30 hover:bg-red-500/25 rounded-lg px-3 disabled:opacity-40 disabled:cursor-not-allowed"><Trash2 className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Excluir</span></Button>
                  <Button type="button" size="sm" data-testid="professor-toggle-button" onClick={() => toggleStatus(p)}
                    className={`h-9 rounded-lg px-3 font-medium ${p.status === "inativo" ? "bg-emerald-500 hover:bg-emerald-600 text-[#0A0D12]" : "bg-slate-700/40 text-slate-200 border border-slate-600/40 hover:bg-slate-700/60"}`}>
                    <Power className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">{p.status === "inativo" ? "Ativar" : "Inativar"}</span>
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Demo accounts */}
      <div data-testid="demo-section" className="pt-2">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
          <div>
            <h2 className="text-lg font-bold font-heading text-slate-100 flex items-center gap-2">
              <Play className="w-5 h-5 text-blue-400" /> Contas Demo
            </h2>
            <p className="text-sm text-slate-400">Acesso temporário de 6h com dados fictícios. Compartilhe o link gerado.</p>
          </div>
          <Button data-testid="create-demo-button" onClick={createDemo} disabled={creatingDemo}
            className="h-11 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-lg px-4">
            {creatingDemo ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Play className="w-4 h-4 mr-1" />} Criar demo
          </Button>
        </div>
        {demos.length === 0 ? (
          <div className="text-center py-8 text-slate-500 text-sm bg-[#0F1520] border border-[#263147] rounded-xl">Nenhuma conta demo ativa.</div>
        ) : (
          <div className="space-y-2">
            {demos.map((d) => (
              <div key={d.user_id} data-testid="demo-row" className="bg-[#0F1520] border border-[#263147] rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-slate-200 text-sm font-medium truncate">{d.user_id}</p>
                  <p className="text-xs text-slate-500">Expira em {formatDateTimeBR(d.plan_end)}</p>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full border ${d.active ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" : "bg-slate-500/15 text-slate-400 border-slate-500/30"}`}>
                  {d.active ? "Ativa" : "Expirada"}
                </span>
                <div className="flex gap-2">
                  <Button type="button" size="sm" data-testid="demo-copy-button" onClick={() => copyDemoLink(d.token)}
                    className="h-9 bg-[#181F2C] border border-[#263147] text-slate-200 hover:bg-[#1E2738] rounded-lg px-3">
                    <Copy className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Copiar link</span>
                  </Button>
                  <Button type="button" size="sm" data-testid="demo-delete-button" onClick={() => removeDemo(d.user_id)}
                    className="h-9 bg-red-500/15 text-red-400 border border-red-500/30 hover:bg-red-500/25 rounded-lg px-3">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-[#0F1520] border-[#263147] text-slate-100">
          <DialogHeader><DialogTitle>Novo professor</DialogTitle><DialogDescription className="text-slate-400">O professor faz login com este e-mail via Google.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label className="text-slate-300 text-xs">Nome *</Label>
              <Input data-testid="new-professor-name" value={createDraft.name} onChange={(e) => setCreateDraft((d) => ({ ...d, name: e.target.value }))} className={selectCls} /></div>
            <div className="space-y-1"><Label className="text-slate-300 text-xs">E-mail (Google) *</Label>
              <Input data-testid="new-professor-email" type="email" value={createDraft.email} onChange={(e) => setCreateDraft((d) => ({ ...d, email: e.target.value }))} className={selectCls} /></div>
            <div className="space-y-1"><Label className="text-slate-300 text-xs">Plano *</Label>
              <Select value={createDraft.plan} onValueChange={(v) => setCreateDraft((d) => ({ ...d, plan: v }))}>
                <SelectTrigger data-testid="new-professor-plan" className={selectCls}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#181F2C] border-[#263147] text-slate-100">
                  {PLAN_OPTIONS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select></div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setCreateOpen(false)} className="border-[#263147] bg-[#181F2C] text-slate-200 hover:bg-[#1E2738]">Cancelar</Button>
            <Button type="button" data-testid="new-professor-save" onClick={createProfessor} className="bg-emerald-500 hover:bg-emerald-600 text-[#0A0D12] font-semibold">Cadastrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editFor} onOpenChange={(o) => !o && setEditFor(null)}>
        <DialogContent className="bg-[#0F1520] border-[#263147] text-slate-100 max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Editar professor</DialogTitle><DialogDescription className="text-slate-400">{editFor?.email}</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label className="text-slate-300 text-xs">Nome</Label>
              <Input data-testid="edit-professor-name" value={editDraft.name || ""} onChange={(e) => setEditDraft((d) => ({ ...d, name: e.target.value }))} className={selectCls} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-slate-300 text-xs">Nome do plano</Label>
                <Select value={editDraft.plan} onValueChange={(v) => setEditDraft((d) => ({ ...d, plan: v }))}>
                  <SelectTrigger data-testid="edit-professor-plan" className={selectCls}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#181F2C] border-[#263147] text-slate-100">
                    {PLAN_OPTIONS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select></div>
              <div className="space-y-1"><Label className="text-slate-300 text-xs">Status</Label>
                <Select value={editDraft.status} onValueChange={(v) => setEditDraft((d) => ({ ...d, status: v }))}>
                  <SelectTrigger data-testid="edit-professor-status" className={selectCls}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#181F2C] border-[#263147] text-slate-100">
                    <SelectItem value="ativo">Ativo</SelectItem>
                    <SelectItem value="inativo">Inativo</SelectItem>
                  </SelectContent>
                </Select></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-slate-300 text-xs">Valor (R$)</Label>
                <Input data-testid="edit-professor-price" type="number" step="0.01" min="0" value={editDraft.plan_price || ""} onChange={(e) => setEditDraft((d) => ({ ...d, plan_price: e.target.value }))} className={selectCls} /></div>
              <div className="space-y-1"><Label className="text-slate-300 text-xs">Período (dias)</Label>
                <Input data-testid="edit-professor-days" type="number" min="0" value={editDraft.plan_days || ""} onChange={(e) => setEditDraft((d) => ({ ...d, plan_days: e.target.value }))} className={selectCls} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-slate-300 text-xs">Data de início</Label>
                <Input data-testid="edit-professor-start" type="date" value={editDraft.plan_start || ""} onChange={(e) => setEditDraft((d) => ({ ...d, plan_start: e.target.value }))} className={selectCls} /></div>
              <div className="space-y-1"><Label className="text-slate-300 text-xs">Data de vencimento</Label>
                <Input data-testid="edit-professor-end" type="date" value={editDraft.plan_end || ""} onChange={(e) => setEditDraft((d) => ({ ...d, plan_end: e.target.value }))} className={selectCls} /></div>
            </div>
            <div className="space-y-1"><Label className="text-slate-300 text-xs">Forma de pagamento</Label>
              <Select value={editDraft.payment_method} onValueChange={(v) => setEditDraft((d) => ({ ...d, payment_method: v }))}>
                <SelectTrigger data-testid="edit-professor-method" className={selectCls}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#181F2C] border-[#263147] text-slate-100">
                  {PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div className="space-y-1"><Label className="text-slate-300 text-xs">Observações</Label>
              <textarea data-testid="edit-professor-notes" value={editDraft.notes || ""} onChange={(e) => setEditDraft((d) => ({ ...d, notes: e.target.value }))}
                rows={3} className="w-full bg-[#181F2C] border border-[#263147] text-slate-100 rounded-lg p-3 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-emerald-500" /></div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setEditFor(null)} className="border-[#263147] bg-[#181F2C] text-slate-200 hover:bg-[#1E2738]">Cancelar</Button>
            <Button type="button" data-testid="edit-professor-save" onClick={saveEdit} disabled={savingEdit} className="bg-emerald-500 hover:bg-emerald-600 text-[#0A0D12] font-semibold">
              {savingEdit ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment dialog */}
      <Dialog open={!!payFor} onOpenChange={(o) => !o && setPayFor(null)}>
        <DialogContent className="bg-[#0F1520] border-[#263147] text-slate-100">
          <DialogHeader><DialogTitle>Registrar pagamento (Pix)</DialogTitle><DialogDescription className="text-slate-400">{payFor?.name} — renova o plano a partir do vencimento atual.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label className="text-slate-300 text-xs">Plano pago *</Label>
              <Select value={payDraft.plan} onValueChange={(v) => setPayDraft((d) => ({ ...d, plan: v }))}>
                <SelectTrigger data-testid="payment-plan-select" className={selectCls}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#181F2C] border-[#263147] text-slate-100">
                  {PAID_PLANS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div className="space-y-1"><Label className="text-slate-300 text-xs">Data do pagamento *</Label>
              <Input data-testid="payment-date" type="date" value={payDraft.date} onChange={(e) => setPayDraft((d) => ({ ...d, date: e.target.value }))} className={selectCls} /></div>
            <div className="space-y-1"><Label className="text-slate-300 text-xs">Valor recebido (R$) *</Label>
              <Input data-testid="payment-amount" type="number" step="0.01" min="0" value={payDraft.amount} onChange={(e) => setPayDraft((d) => ({ ...d, amount: e.target.value }))} placeholder="0,00" className={selectCls} /></div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setPayFor(null)} className="border-[#263147] bg-[#181F2C] text-slate-200 hover:bg-[#1E2738]">Cancelar</Button>
            <Button type="button" data-testid="payment-save" onClick={savePayment} className="bg-emerald-500 hover:bg-emerald-600 text-[#0A0D12] font-semibold">Registrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteFor} onOpenChange={(o) => !o && setDeleteFor(null)}>
        <AlertDialogContent className="bg-[#0F1520] border-[#263147] text-slate-100">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-slate-100">Excluir professor?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Esta ação removerá <span className="text-slate-200 font-medium">{deleteFor?.name}</span> e todos os dados vinculados (alunos, agenda, treinos e avaliações). Não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#263147] bg-[#181F2C] text-slate-200 hover:bg-[#1E2738]">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="professor-delete-confirm" onClick={confirmDelete} className="bg-red-500 hover:bg-red-600 text-white">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
